import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

export type Theme = "system" | "light" | "dark";

export interface BusinessInfo {
  businessName: string;
  businessAddress: string;
  businessPhone: string;
  businessEmail: string;
}

interface UiState {
  theme: Theme;
  customVariables: Record<string, string>;
  companyLogoUrl: string | null;
  businessInfo: BusinessInfo;
}

const loadFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

const saveToStorage = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
  }
};


// Helper to read all CSS variables from :root and .dark
const getCssVariablesFromRoot = (): Record<string, string> => {
  const vars: Record<string, string> = {};

  if (typeof document !== 'undefined') {
    const root = document.documentElement;

    // Get light mode variables from :root
    const lightStyles = getComputedStyle(root);
    const lightVars: Record<string, string> = {};
    for (let i = 0; i < lightStyles.length; i++) {
      const prop = lightStyles[i];
      if (prop.startsWith('--')) {
        lightVars[prop] = lightStyles.getPropertyValue(prop).trim();
      }
    }

    // Get dark mode variables by temporarily adding .dark class
    const hadDarkClass = root.classList.contains('dark');
    if (!hadDarkClass) {
      root.classList.add('dark');
    }

    const darkStyles = getComputedStyle(root);
    const darkVars: Record<string, string> = {};
    for (let i = 0; i < darkStyles.length; i++) {
      const prop = darkStyles[i];
      if (prop.startsWith('--')) {
        const darkValue = darkStyles.getPropertyValue(prop).trim();
        const lightValue = lightVars[prop];

        // Only store if different from light mode
        if (darkValue !== lightValue) {
          darkVars[prop] = darkValue;
        }
      }
    }

    if (!hadDarkClass) {
      root.classList.remove('dark');
    }

    // Combine into flat structure with .dark: separator for dual-mode variables
    Object.keys(lightVars).forEach(key => {
      const lightValue = lightVars[key];
      const darkValue = darkVars[key];

      if (darkValue && darkValue !== lightValue) {
        // Both light and dark values exist and differ - use .dark: separator
        vars[key] = `${lightValue} .dark: ${darkValue}`;
      } else {
        // Only light value or values are the same
        vars[key] = lightValue;
      }
    });
  }

return vars;
};

const defaultBusinessInfo: BusinessInfo = {
  businessName: '',
  businessAddress: '',
  businessPhone: '',
  businessEmail: '',
};

const initialState: UiState = {
  theme: loadFromStorage<Theme>('vite-ui-theme', 'system'),
  customVariables: typeof document !== 'undefined' ? getCssVariablesFromRoot() : {},
  companyLogoUrl: null,
  businessInfo: defaultBusinessInfo,
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
      saveToStorage('vite-ui-theme', action.payload);
    },
    setCssVariables: (state, action: PayloadAction<Record<string, string>>) => {
      state.customVariables = { ...state.customVariables, ...action.payload };
      saveToStorage('vite-ui-custom-variables', state.customVariables);
    },
resetCssVariables: (state) => {
      state.customVariables = {};
      localStorage.removeItem('vite-ui-custom-variables');
    },
    setCompanyLogo: (state, action: PayloadAction<string | null>) => {
      state.companyLogoUrl = action.payload;
    },
    setBusinessInfo: (state, action: PayloadAction<Partial<BusinessInfo>>) => {
      state.businessInfo = { ...state.businessInfo, ...action.payload };
    },
  },
});

export const { setTheme, setCssVariables, resetCssVariables, setCompanyLogo, setBusinessInfo } = uiSlice.actions;
export default uiSlice.reducer;
